import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import { useSubscription } from "shared/contexts/Subscription";
import ContentHeaderSection from "../../shared/common/ContentHeaderSection";
import KebabMenu from "shared/common/KebabMenu";
import FeatureLimitDisplay from "shared/common/FeatureLimitDisplay";
import apiCall from "shared/services/ApiAdapter";
import masterApiCall from "shared/services/MasterDataApi";
import { getCurrentUserId } from "shared/utils/SessionDataUtils";
import { loadMathJaxScript } from "../assessment/loadMathJaxScript";
import { ProtectedVisibility } from "shared/security/ProtectedRoute";
import { ShowQuestions } from "modules/questions/ShowQuestions";

// =========================================================================================
// --- NEW COMPONENT: Quiz Mode Selection Modal (Grayscale Styling) ---
const QuizModeSelectionModal = ({ evaluation, onClose, onSelectMode, t }) => {
  if (!evaluation) return null;

  const handlePresentation = () => {
    // 'presentation' is the mode identifier
    onSelectMode(evaluation, "presentation");
  };

  const handleInteractive = () => {
    // 'interactive' is the mode identifier
    onSelectMode(evaluation, "interactive");
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100">
        <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100 border-b pb-2 border-gray-300 dark:border-gray-600">
          Launch Quiz: {evaluation.name}
        </h3>
        <p className="mb-6 text-gray-700 dark:text-gray-300">
          Select the quiz mode for {evaluation.name}
        </p>

        <div className="space-y-4">
          <button
            onClick={handlePresentation}
            className="w-full flex items-center justify-center gap-2 
                    bg-gray-700 text-white 
                    py-3 px-4 rounded-lg font-semibold text-lg 
                    hover:bg-gray-900 
                    dark:bg-gray-600 dark:hover:bg-gray-700
                    transition-colors shadow-md"
          >
            <span className="text-xl">📺</span> Presentation Mode
          </button>
          <button
            onClick={handleInteractive}
            className="w-full flex items-center justify-center gap-2 
                    bg-gray-500 text-white 
                    py-3 px-4 rounded-lg font-semibold text-lg 
                    hover:bg-gray-600 
                    dark:bg-gray-500 dark:hover:bg-gray-600
                    transition-colors shadow-md"
          >
            <span className="text-xl">🧑‍💻</span> Interactive Mode
          </button>
        </div>

        <button
          onClick={onClose}
          className="mt-6 w-full text-gray-600 dark:text-gray-400 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
// =========================================================================================


// --- Helper: Group and Sort by Course ---
const groupAndSortEvaluations = (allEvaluations) => {
  const grouped = allEvaluations.reduce((acc, evaluation) => {
    const courseId = evaluation.course?.id;
    if (!courseId) return acc;

    if (!acc[courseId]) {
      acc[courseId] = [];
    }
    acc[courseId].push(evaluation);
    return acc;
  }, {});

  for (const courseId in grouped) {
    grouped[courseId].sort(
      (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
    );
  }

  return grouped;
};

// --- Sub-Component: Evaluation Card ---
// PROP LIST UPDATED: Added handleLaunchQuizSelection
const EvaluationCard = ({
  evaluation,
  navigateTo,
  handleViewQuestions,
  handleAssignToBatch,
  handleLaunchQuizSelection,
}) => {
  return (
    <div
      onClick={() => navigateTo("viewAssignments", evaluation.id)}
      className="relative flex flex-col items-start p-6 
bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800
rounded-xl border border-gray-300 dark:border-gray-700
shadow-sm hover:shadow-md hover:scale-[1.01]
transition-transform duration-300 ease-out cursor-pointer"
    >
      {/* Action buttons */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <ProtectedVisibility requiredRoles={["ROLE_TEACHER"]} beta={true}>
          <button
            // MODIFIED: Calls the new handler to show the mode selection popup
            onClick={(e) => {
              e.stopPropagation();
              handleLaunchQuizSelection(evaluation);
            }}
            className="p-2 text-gray-500 dark:text-gray-400 
 hover:text-gray-900 dark:hover:text-gray-200
rounded-full transition-colors
hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Start Quiz"
            aria-label="Start Quiz"
          >
            <div className="relative inline-block">
              <span className="text-lg font-semibold">Quiz</span>
              <span
                className="absolute -top-2 -right-6  px-2 py-0.5 text-xs font-small tracking-wide font-bold
 text-gray-800 bg-gray-100 rounded-full dark:bg-gray-700 dark:text-gray-300
transform rotate-12 shadow-sm"
              >
                beta
              </span>
            </div>
          </button>
        </ProtectedVisibility>

        <button
          onClick={(e) => handleViewQuestions(evaluation, e)}
          className="p-2 text-gray-500 dark:text-gray-400 
 hover:text-gray-900 dark:hover:text-gray-200
 rounded-full transition-colors
 hover:bg-gray-100 dark:hover:bg-gray-700"
          title="View Questions"
          aria-label="View Questions"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
        </button>

        <KebabMenu onAssignToBatch={() => handleAssignToBatch(evaluation)} />
      </div>

      {/* Title */}
      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 tracking-wide mb-1 pr-16">
        {evaluation.name}
      </h3>

      {/* Meta info */}
      <p className="text-sm text-gray-700 dark:text-gray-400">
        {evaluation.conceptTopic || "-"} • {evaluation.difficulty}
      </p>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {evaluation.numberOfQuestions} Qs • {evaluation.maxTimeMinutes} mins
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">
        Due: {evaluation.expiryDateTime?.split("T")[0]}
      </p>

      {/* Practice badge */}
      {evaluation.isPractice && (
        <span
          className="mt-4 text-xs px-2 py-1 
 bg-gray-300 text-gray-900 
 dark:bg-gray-700 dark:text-gray-200
 rounded-full font-medium tracking-wide"
        >
          PRACTICE
        </span>
      )}
    </div>
  );
};

// --- Main Component ---
const EvaluationsContent = ({ navigateTo, useMockData, displayToast, showQuestions }) => {
  const [courses, setCourses] = useState([]);
  const [evaluations, setEvaluations] = useState([]);

  // State for expanding sections
  const [expandedCourses, setExpandedCourses] = useState({});
  const [isRecentExpanded, setIsRecentExpanded] = useState(false);

  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // NEW STATE: To control the Quiz Mode Selection Modal
  const [quizModeSelectionEvaluation, setQuizModeSelectionEvaluation] = useState(null);

  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const { limits, canPerformAction } = useSubscription();
  const userId = getCurrentUserId();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // ... (Effect hooks for fetching courses and evaluations remain unchanged) ...

  // Fetch Courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const url = `/courses?isActive.equals=true&createdBy.equals=${userId}&size=10000`;
        const data = await apiCall(
          url,
          useMockData,
          "GET",
          null,
          startLoading,
          stopLoading,
          displayToast
        );
        setCourses(data || []);
      } catch (error) {
        console.error("Failed to fetch courses:", error);
      }
    };

    if (userId) {
      fetchCourses();
    }
  }, [userId]);

  // Fetch Evaluations
  useEffect(() => {
    const getEvaluations = async () => {
      try {
        const data = await apiCall(
          `/evaluations?createdBy.specified=true&isActive.equals=true&createdBy.equals=${userId}&size=10000`,
          useMockData,
          "GET",
          null,
          startLoading,
          stopLoading,
          displayToast
        );
        setEvaluations(data || []);
      } catch (error) {
        console.error("Failed to fetch evaluations:", error);
      }
    };

    if (courses.length > 0 && userId) {
      getEvaluations();
    }

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [courses, userId]);


  // --- UPDATED LOGIC: Recent Evaluations ---
  const recentEvaluations = useMemo(() => {
    if (!evaluations || evaluations.length === 0) return [];

    // 1. Sort all evaluations by date (Newest First)
    const sorted = [...evaluations].sort((a, b) => {
      const dateA = a.createdDate ? new Date(a.createdDate) : new Date(0);
      const dateB = b.createdDate ? new Date(b.createdDate) : new Date(0);
      return dateB - dateA;
    });

    // 2. Filter for items strictly within the last 2 weeks
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    const lastTwoWeeksDocs = sorted.filter(e => new Date(e.createdDate) >= twoWeeksAgo);

    // 3. Rule: If data exists in last 2 weeks, show it.
    //If NOT, show the last 3 created as a fallback.
    if (lastTwoWeeksDocs.length > 0) {
      return lastTwoWeeksDocs;
    } else {
      return sorted.slice(0, 3);
    }
  }, [evaluations]);

  // 2. Group logic for Course lists
  const groupedEvaluations = useMemo(() => {
    return groupAndSortEvaluations(evaluations);
  }, [evaluations]);

  const handleAssignToBatch = (evaluation) => {
    navigateTo("assignEvaluationToBatch", null, null, evaluation.id);
  };

  // NEW HANDLER: Opens the quiz mode selection modal
  const handleLaunchQuizSelection = (evaluation) => {
    setQuizModeSelectionEvaluation(evaluation);
  };

  // NEW HANDLER: Closes the quiz mode selection modal
  const handleCloseQuizSelection = () => {
    setQuizModeSelectionEvaluation(null);
  };

  // NEW HANDLER: Navigates after a mode is selected
  const handleStartQuiz = (evaluationId, mode) => {
    handleCloseQuizSelection(); // Close the modal
    // Navigate, passing the mode as a parameter (e.g., query param in a real app)
    // Assuming navigateTo signature is (route, id, params)
    navigateTo("startQuiz", evaluationId, mode);
  };


  const handleViewQuestions = useCallback(
    async (evaluation, e) => {
      e.stopPropagation(); // Prevent card click
      setSelectedEvaluation(evaluation);
      setLoadingQuestions(true);
      setQuestions([]);
      loadMathJaxScript();

      try {
        // Get question codes from evaluation
        let questionCodes =
          evaluation?.questionIds || evaluation?.questionCodes || [];
        if (typeof questionCodes === "string") {
          questionCodes = questionCodes
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }

        if (Array.isArray(questionCodes) && questionCodes.length > 0) {
          const res = await masterApiCall(
            "/questions/by-codes",
            useMockData,
            "POST",
            { questionCodes },
            startLoading,
            stopLoading,
            displayToast
          );
          const fetchedQuestions = Array.isArray(res)
            ? res
            : res?.content ?? [];
          setQuestions(fetchedQuestions);
        } else {
          if (typeof displayToast === "function") {
            displayToast("No questions found for this evaluation", "info");
          }
          setSelectedEvaluation(null);
        }
      } catch (err) {
        console.error("Failed to fetch questions:", err);
        if (typeof displayToast === "function") {
          displayToast("Failed to load questions", "error");
        }
        setSelectedEvaluation(null);
      } finally {
        setLoadingQuestions(false);
      }
    },
    [useMockData, startLoading, stopLoading, displayToast]
  );

  const handleCloseQuestionsModal = () => {
    setSelectedEvaluation(null);
    setQuestions([]);
  };

  const toggleCourseExpansion = (courseId) => {
    setExpandedCourses((prev) => ({
      ...prev,
      [courseId]: !prev[courseId],
    }));
  };

  const getVisibleCardCount = () => {
    if (windowWidth >= 1024) {
      return 3;
    } else if (windowWidth >= 640) {
      return 2;
    } else {
      return 1;
    }
  };

  const canCreateEvaluation = canPerformAction(
    "maxEvaluationsPerTutorLimit",
    evaluations?.length ?? 0
  );

  const rightSideContentTop = (
    <>
      <div className="relative w-full max-w-40">
        <input
          type="text"
          placeholder={t("search")}
          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 text-gray-900"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
          🔍
        </span>
      </div>
      <div className="flex items-center justify-center w-10 h-10 text-xl text-gray-100 bg-gray-800 rounded-full cursor-pointer dark:bg-gray-600 dark:text-gray-200">
        ⚙️
      </div>
      <button
        onClick={() => navigateTo("createEvaluation")}
        className={`px-5 py-2 rounded-lg font-medium transition-colors ${canCreateEvaluation
          ? "bg-gray-800 text-gray-100 hover:bg-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500"
          : "bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400"
          }`}
        disabled={!canCreateEvaluation}
      >
        {t("createEvaluation")}
      </button>
    </>
  );

  // Calculate Recent Items to Show based on Toggle
  const visibleCardCount = getVisibleCardCount();
  const recentToShow = isRecentExpanded
    ? recentEvaluations
    : recentEvaluations.slice(0, visibleCardCount);
  const hasMoreRecent = recentEvaluations.length > visibleCardCount;

  return (
    <div className="p-8 md:px-10 ">
      <ContentHeaderSection
        title={t("evaluations")}
        subtitle={t("manageReviewEvaluations")}
        rightContentTop={rightSideContentTop}
        rightContentBottom={
          <FeatureLimitDisplay
            feature="maxEvaluationsPerTutor"
            currentCount={evaluations.length}
            limit={limits.maxEvaluationsPerTutor}
          />
        }
      />

      {/* --- RECENT EVALUATIONS SECTION --- */}
      {recentEvaluations.length > 0 && (
        <div className="mb-10 border-b-2 border-dashed border-gray-200 dark:border-gray-700 pb-2">
          <div className="flex justify-between items-center pb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <span className="text-2xl">🕒</span>{" "}
              {t("recentEvaluations") || "Recent Evaluations"}
            </h2>

            {/* View All / View Less Toggle for Recent */}
            {hasMoreRecent && (
              <div
                className="relative group cursor-pointer"
                onClick={() => setIsRecentExpanded(!isRecentExpanded)}
              >
                <span className="relative z-10 text-sm font-medium text-blue-600 transition-all duration-300 group-hover:text-blue-800 dark:text-blue-400 dark:group-hover:text-blue-200">
                  {isRecentExpanded
                    ? `View Less (${recentEvaluations.length})`
                    : `View All (${recentEvaluations.length - visibleCardCount})`}
                </span>
                {/* Stacked cards effect */}
                <span className="absolute inset-0 z-0 bg-blue-100 rounded-sm -top-1 left-2 w-10 h-6 transform rotate-3 origin-bottom-right transition-all duration-300 group-hover:rotate-6 group-hover:translate-x-1 dark:bg-blue-900"></span>
                <span className="absolute inset-0 z-0 bg-blue-200 rounded-sm top-0 left-1 w-10 h-6 transform -rotate-3 origin-top-left transition-all duration-300 group-hover:rotate-0 group-hover:-translate-x-1 dark:bg-blue-800"></span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {recentToShow.map((evaluation) => (
              <EvaluationCard
                key={`recent-${evaluation.id}`}
                evaluation={evaluation}
                navigateTo={navigateTo}
                handleViewQuestions={handleViewQuestions}
                handleAssignToBatch={handleAssignToBatch}
                // PASS NEW HANDLER
                handleLaunchQuizSelection={handleLaunchQuizSelection}
              />
            ))}
          </div>
        </div>
      )}
      {/* --- EXISTING COURSE LISTS --- */}
      {Object.keys(groupedEvaluations).length === 0 ? (
        <div className="col-span-full text-center text-gray-600 dark:text-gray-400 mt-10">
          {t("noEvaluationsFound")}
        </div>
      ) : (
        <div className="space-y-12">
          {Object.entries(groupedEvaluations).map(
            ([courseId, courseEvaluations]) => {
              const isExpanded = expandedCourses[courseId];
              const evaluationsToShow = isExpanded
                ? courseEvaluations
                : courseEvaluations.slice(0, visibleCardCount);
              const hasMore = courseEvaluations.length > visibleCardCount;
              const courseName = courses.find(
                (c) => c.id == courseId
              )?.courseName;

              return (
                <div key={courseId} className="space-y-6">
                  <div className="flex justify-between items-center pb-2 border-b-2 border-gray-200 dark:border-gray-700">
                    <h2 className="flex items-center text-2xl font-bold text-gray-900 dark:text-gray-100">
                      <span className="px-3 py-1 mr-3 text-sm font-medium tracking-wide text-gray-800 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-gray-300">
                        Course
                      </span>
                      {courseName}
                    </h2>
                    {hasMore && (
                      <div
                        className="relative group cursor-pointer"
                        onClick={() => toggleCourseExpansion(courseId)}
                      >
                        {/* Top card */}
                        <span className="relative z-10 text-sm font-medium text-blue-600 transition-all duration-300 group-hover:text-blue-800 dark:text-blue-400 dark:group-hover:text-blue-200">
                          {isExpanded
                            ? `View Less (${courseEvaluations.length})`
                            : `View All (${courseEvaluations.length - visibleCardCount
                            })`}
                        </span>
                        {/* Stacked cards visualization */}
                        <span className="absolute inset-0 z-0 bg-blue-100 rounded-sm -top-1 left-2 w-10 h-6 transform rotate-3 origin-bottom-right transition-all duration-300 group-hover:rotate-6 group-hover:translate-x-1 dark:bg-blue-900"></span>
                        <span className="absolute inset-0 z-0 bg-blue-200 rounded-sm top-0 left-1 w-10 h-6 transform -rotate-3 origin-top-left transition-all duration-300 group-hover:rotate-0 group-hover:-translate-x-1 dark:bg-blue-800"></span>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {evaluationsToShow.map((evaluation) => (
                      <EvaluationCard
                        key={evaluation.id}
                        evaluation={evaluation}
                        navigateTo={navigateTo}
                        handleViewQuestions={handleViewQuestions}
                        handleAssignToBatch={handleAssignToBatch}
                        // PASS NEW HANDLER
                        handleLaunchQuizSelection={handleLaunchQuizSelection}
                      />
                    ))}
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}

      {/* Questions View Modal */}
      {selectedEvaluation && (
        ShowQuestions(selectedEvaluation, questions, handleCloseQuestionsModal, loadingQuestions, t)
      )}

      {/* NEW: Quiz Mode Selection Modal */}
      {quizModeSelectionEvaluation && (
        <QuizModeSelectionModal
          evaluation={quizModeSelectionEvaluation}
          onClose={handleCloseQuizSelection}
          onSelectMode={handleStartQuiz}
          t={t}
        />
      )}

    </div>
  );
};

export default EvaluationsContent;

